import React, { useCallback, useEffect, useRef, useState } from "react";
import { memo } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";

import Navigation from "./components/Navigation";
import { getStepInfo, LIST_HEIGHT, ROW_HEIGHT, STEPS } from "./constants";
import ColumnMappingStep from "./steps/ColumnMappingStep";
import DataValidationStep from "./steps/DataValidationStep";
import FileUploadStep from "./steps/FileUploadStep";
import { parseFile } from "./utils/fileProcessing";

const MemoizedFileUploadStep = memo(FileUploadStep);
const MemoizedColumnMappingStep = memo(ColumnMappingStep);
const MemoizedDataValidationStep = memo(DataValidationStep);
const MemoizedNavigation = memo(Navigation);

const BulkImportWizard = ({
  expectedColumns = [
    { key: "name", label: "İsim", required: true },
    { key: "email", label: "E-posta", required: true },
    { key: "phone", label: "Telefon", required: false },
  ],
  onComplete,
  title = "Toplu Veri Yükleme",
  description = "CSV veya Excel dosyasından toplu veri yükleme",
  templateUrl,
}) => {
  // State
  const { t } = useTranslation();
  const [step, setStep] = useState(STEPS.FILE_UPLOAD);
  const [file, setFile] = useState(null);
  const [rawData, setRawData] = useState([]);
  const [previewData, setPreviewData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [validationErrors, setValidationErrors] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processedData, setProcessedData] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Progress tracking for validation
  const [validationProgress, setValidationProgress] = useState(0);
  const [firstErrorInfo, setFirstErrorInfo] = useState(null);
  // Flag to track if data has been modified after validation
  const [isDataDirty, setIsDataDirty] = useState(false);

  const tableRef = useRef(null);
  // const [tableWidth, setTableWidth] = useState(0); // Seems unused, commenting out

  // Web Worker reference
  const dataProcessorWorker = useRef(null);
  // Validation Worker reference
  const validationWorker = useRef(null);
  // Debounce timer for progress updates
  const progressUpdateTimer = useRef(null);

  // Filtreleme için kullanılacak veri - Tüm işlenmiş veriyi göster, hata olsa bile
  const filteredData = processedData;

  // Boş tablo ile başlama durumunda
  useEffect(() => {
    if (step === STEPS.DATA_VALIDATION && !file) {
      // Boş kolon eşleştirmesi başlat
      const initialMapping = {};
      expectedColumns.forEach((col) => {
        initialMapping[col.key] = col.key; // Doğrudan anahtar değerini kullan
      });
      setColumnMapping(initialMapping);

      // Başlıkları ayarla
      const headerValues = expectedColumns.map((col) => col.key);
      setHeaders(headerValues);
    }
  }, [step, file, expectedColumns]);

  // Initialize and terminate the Web Worker
  useEffect(() => {
    // Create worker
    // Note: Ensure your build setup handles .worker.js files correctly (e.g., worker-loader for Webpack)
    dataProcessorWorker.current = new Worker(
      new URL("./utils/dataProcessor.worker.js", import.meta.url),
    );

    // Handle messages from worker
    dataProcessorWorker.current.onmessage = (event) => {
      const { type, payload } = event.data;
      if (type === "RESULT") {
        setProcessedData(payload.processedData);
        setValidationErrors(payload.validationErrors);
        setIsProcessing(false);
        setStep(STEPS.DATA_VALIDATION);
      } else if (type === "ERROR") {
        console.error("Worker Error:", payload);
        showToast(
          "error",
          "İşleme Hatası",
          payload.message || "Veriler işlenirken bir hata oluştu (worker)",
        );
        setIsProcessing(false);
      }
    };

    // Handle errors from worker
    dataProcessorWorker.current.onerror = (error) => {
      console.error(`Worker error: ${error.message}`, error);
      showToast(
        "error",
        "Worker Hatası",
        "Arka planda işlem yapılırken beklenmedik bir hata oluştu.",
      );
      setIsProcessing(false);
    };

    // Initialize validation worker
    validationWorker.current = new Worker(
      new URL("./utils/dataValidation.worker.js", import.meta.url),
    );

    // Handle messages from validation worker
    validationWorker.current.onmessage = (event) => {
      const { type, payload } = event.data;

      if (type === "PROGRESS") {
        // Debounce progress updates to reduce render frequency
        if (progressUpdateTimer.current) {
          clearTimeout(progressUpdateTimer.current);
        }

        progressUpdateTimer.current = setTimeout(() => {
          setValidationProgress(payload.progress);

          // Optionally update partial results for large datasets
          if (payload.currentErrors && payload.currentErrors.length > 0) {
            // Only update if we have new errors to show
            setValidationErrors((prev) => {
              // Avoid duplicate errors by checking row/column combinations
              const newErrors = payload.currentErrors.filter(
                (newError) =>
                  !prev.some(
                    (existingError) =>
                      existingError.row === newError.row &&
                      existingError.column === newError.column,
                  ),
              );
              return [...prev, ...newErrors];
            });
          }
        }, 100); // 100ms debounce
      } else if (type === "RESULT") {
        // Final results
        setProcessedData(payload.processedData);
        setValidationErrors(payload.validationErrors);
        setFirstErrorInfo(payload.firstErrorInfo);
        setValidationProgress(100);
        setIsProcessing(false);
        setIsDataDirty(false); // Reset dirty flag after validation
        setStep(STEPS.DATA_VALIDATION);
      } else if (type === "ERROR") {
        console.error("Validation Worker Error:", payload);
        showToast(
          "error",
          "Doğrulama Hatası",
          payload.message || "Veriler doğrulanırken bir hata oluştu",
        );
        setIsProcessing(false);
      }
    };

    // Handle errors from validation worker
    validationWorker.current.onerror = (error) => {
      console.error(`Validation worker error: ${error.message}`, error);
      showToast(
        "error",
        "Doğrulama Hatası",
        "Veri doğrulama işlemi sırasında beklenmedik bir hata oluştu.",
      );
      setIsProcessing(false);
    };

    // Cleanup: Terminate worker when component unmounts
    return () => {
      if (dataProcessorWorker.current) {
        dataProcessorWorker.current.terminate();
      }
      if (validationWorker.current) {
        validationWorker.current.terminate();
      }
      if (progressUpdateTimer.current) {
        clearTimeout(progressUpdateTimer.current);
      }
    };
  }, []); // Run only once on mount

  // Dosya değiştiğinde state'i sıfırla
  useEffect(() => {
    if (file) {
      setHeaders([]);
      setColumnMapping({});
      setValidationErrors([]);
      setProcessedData([]);
    }
  }, [file]);

  // Dosya yükleme işlemi
  const onDrop = useCallback((acceptedFiles) => {
    const selectedFile = acceptedFiles[0];
    if (!selectedFile) return;

    // Dosya türünü doğrula
    const extension = selectedFile.name.split(".").pop()?.toLowerCase();
    if (!["csv", "xlsx", "xls"].includes(extension)) {
      showToast(
        "error",
        "Geçersiz dosya formatı",
        "Lütfen .csv, .xlsx veya .xls formatında bir dosya yükleyin",
      );
      return;
    }

    setFile(selectedFile);
    handleParseFile(selectedFile, true);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-excel": [".xls"],
    },
    multiple: false,
  });

  // Toast mesajı gösterme yardımcı fonksiyonu
  const showToast = (type, title, message) => {
    // Basit bir alert göster (gerçek uygulamada toast bileşeni kullanılabilir)
    console.log(`[${type}] ${title}: ${message}`);
    if (type === "error") {
      alert(`${title}: ${message}`);
    }
  };

  // Dosya içeriğini ayrıştır
  const handleParseFile = useCallback(
    (file, autoAdvance = false) => {
      setIsProcessing(true);

      parseFile(
        file,
        (data) => {
          setRawData(data);
          // İlk 5 satırı önizleme olarak göster
          setPreviewData(data.slice(0, 5));

          // İlk satırı header olarak kullan
          if (data.length > 0) {
            setHeaders(data[0]);

            // Boş kolon eşleştirmesi başlat
            const initialMapping = {};
            expectedColumns.forEach((col) => {
              // Otomatik eşleştirme dene
              const matchingHeader = data[0].find(
                (header) =>
                  header && header.toLowerCase() === col.label.toLowerCase(),
              );
              initialMapping[col.key] = matchingHeader || "";
            });
            setColumnMapping(initialMapping);
          }

          setIsProcessing(false);

          // Dosya yüklendikten sonra otomatik olarak bir sonraki adıma geç
          if (autoAdvance) {
            setStep(STEPS.COLUMN_MAPPING);
          }
        },
        (error) => {
          console.error("File parse error:", error);
          showToast(
            "error",
            t("common.error"),
            error.message || t("bulkImport.fileReadError"),
          );
          setIsProcessing(false);
        },
      );
    },
    [expectedColumns, setStep],
  );

  // Kolon eşleştirme değişikliklerini işle
  const updateColumnMapping = useCallback((expectedKey, fileHeader) => {
    setColumnMapping((prev) => ({
      ...prev,
      [expectedKey]: fileHeader === "_placeholder_" ? "" : fileHeader,
    }));
  }, []);

  // Eşleştirmenin tamamlanıp tamamlanmadığını kontrol et
  const isMappingComplete = useCallback(() => {
    return expectedColumns
      .filter((col) => col.required)
      .every((col) => columnMapping[col.key] && columnMapping[col.key] !== "");
  }, [columnMapping, expectedColumns]);

  // Veriyi kolon eşleştirmesine göre işle
  const processData = useCallback(() => {
    setIsProcessing(true);
    setValidationProgress(0);
    setValidationErrors([]);
    setFirstErrorInfo(null);

    try {
      // If starting with an empty table, skip worker processing
      if (!file) {
        // Boş tablo durumunda boş veri oluştur
        setProcessedData([]);
        setValidationErrors([]);
        setIsProcessing(false);
        setIsDataDirty(false); // Reset dirty flag
        setStep(STEPS.DATA_VALIDATION);
        return; // Exit early
      }

      // Ensure RegExp patterns are serializable
      const serializableExpectedColumns = expectedColumns.map((col) => {
        if (col.pattern && col.pattern.regex instanceof RegExp) {
          // Convert RegExp to strings for worker
          return {
            ...col,
            pattern: {
              ...col.pattern,
              regex: {
                source: col.pattern.regex.source,
                flags: col.pattern.regex.flags,
              },
            },
          };
        }
        return col;
      });

      // Use the validation worker instead of dataProcessor worker
      validationWorker.current.postMessage({
        rawData,
        headerIndex: 0, // Assuming header is always the first row
        headers,
        columnMapping,
        expectedColumns: serializableExpectedColumns,
      });
    } catch (error) {
      // This catch block might be less likely to trigger now,
      console.error("Data processing error:", error);
      showToast("error", "İşleme Hatası", "Veriler işlenirken bir hata oluştu");
    }
  }, [rawData, headers, columnMapping, expectedColumns, file]); // Added 'file' dependency

  // Form gönderimi
  const handleSubmit = useCallback(async () => {
    if (processedData.length === 0 && validationErrors.length === 0) {
      showToast("error", "Veri Yok", "Yüklenecek veri bulunamadı");
      return;
    }

    // If data is dirty, force validation before submission
    if (isDataDirty) {
      showToast(
        "error",
        "Doğrulama Gerekli",
        "Veri değişikliği yapıldı. Lütfen tekrar doğrulama yapın.",
      );
      return;
    }

    if (validationErrors.length > 0) {
      showToast("error", "Doğrulama Hataları", "Lütfen hataları düzeltin");
      return;
    }

    setIsSubmitting(true);

    try {
      if (onComplete) {
        await onComplete(processedData);
        showToast("success", "Başarılı", "Veriler başarıyla yüklendi");

        // State'i sıfırla
        setFile(null);
        setRawData([]);
        setPreviewData([]);
        setHeaders([]);
        setColumnMapping({});
        setValidationErrors([]);
        setProcessedData([]);
        setIsDataDirty(false);
        setStep(STEPS.FILE_UPLOAD);
      }
    } catch (error) {
      console.error("Submit error:", error);
      showToast("error", "Hata", "Veriler yüklenirken bir hata oluştu");
    } finally {
      setIsSubmitting(false);
    }
  }, [processedData, validationErrors, onComplete]);

  const handleRevalidate = useCallback(() => {
    setIsProcessing(true);
    setValidationProgress(0);
    setValidationErrors([]);
    setFirstErrorInfo(null);
    setIsDataDirty(false); // Reset dirty flag when revalidating

    // Validation worker'a veriyi gönder
    if (validationWorker.current) {
      // Ensure RegExp patterns are serializable
      const serializableExpectedColumns = expectedColumns.map((col) => {
        if (col.pattern && col.pattern.regex instanceof RegExp) {
          // Convert RegExp to strings for worker
          return {
            ...col,
            pattern: {
              ...col.pattern,
              regex: {
                source: col.pattern.regex.source,
                flags: col.pattern.regex.flags,
              },
            },
          };
        }
        return col;
      });

      validationWorker.current.postMessage({
        type: "VALIDATE",
        payload: {
          data: processedData,
          columnMapping,
          expectedColumns: serializableExpectedColumns,
        },
      });
    }
  }, [processedData, columnMapping, expectedColumns, validationWorker]);

  // Wrapped setProcessedData that marks data as dirty when modified
  const setProcessedDataWithDirtyFlag = useCallback((updater) => {
    setProcessedData(updater);
    setIsDataDirty(true);
  }, []);

  // Adım içeriğini render et
  const renderStepContent = () => {
    switch (step) {
      case STEPS.FILE_UPLOAD:
        return (
          <MemoizedFileUploadStep
            onDrop={onDrop}
            file={file}
            isDragActive={isDragActive}
            isProcessing={isProcessing}
            getRootProps={getRootProps}
            getInputProps={getInputProps}
            onContinueWithEmptyTable={() => setStep(STEPS.DATA_VALIDATION)}
            templateUrl={templateUrl}
          />
        );

      case STEPS.COLUMN_MAPPING:
        return (
          <MemoizedColumnMappingStep
            expectedColumns={expectedColumns}
            headers={headers}
            columnMapping={columnMapping}
            updateColumnMapping={updateColumnMapping}
            previewData={previewData}
            selectedHeaderIndex={0}
          />
        );

      case STEPS.DATA_VALIDATION:
        return (
          <MemoizedDataValidationStep
            tableRef={tableRef}
            // tableWidth={tableWidth}
            columnMapping={columnMapping}
            expectedColumns={expectedColumns}
            filteredData={filteredData}
            validationErrors={validationErrors}
            validationProgress={validationProgress}
            firstErrorInfo={firstErrorInfo}
            isProcessing={isProcessing}
            isSubmitting={isSubmitting}
            onSubmit={handleSubmit}
            ROW_HEIGHT={ROW_HEIGHT}
            LIST_HEIGHT={LIST_HEIGHT}
            setProcessedData={setProcessedDataWithDirtyFlag}
            onRevalidate={handleRevalidate}
            isDataDirty={isDataDirty}
          />
        );
      default:
        return null;
    }
  };

  // Adım bilgisini al
  const stepInfo = getStepInfo(step);

  return (
    <div className="mt-4 flex h-full w-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      {/* Header */}
      <div className="border-b bg-gray-50 p-4">
        <h1 className="text-xl font-semibold text-gray-800">{title}</h1>
        <p className="text-sm text-gray-500">{description}</p>
      </div>

      {/* Content */}
      <div className="max-h-[calc(100vh-200px)] flex-grow overflow-auto p-6">
        {/* Adım başlığı ve açıklaması */}
        <div className="mb-2">
          <h2 className="text-lg font-semibold text-gray-800">
            {stepInfo.title}
          </h2>
          <p className="text-sm text-gray-500">{stepInfo.description}</p>
        </div>

        {/* Adım içeriği */}
        {renderStepContent()}
      </div>

      {/* Footer */}
      <div className="border-t bg-gray-50 p-4">
        {isProcessing && step === STEPS.COLUMN_MAPPING && (
          <div className="mb-4">
            <div className="h-2.5 w-full rounded-full bg-gray-200">
              <div
                className="h-2.5 rounded-full bg-blue-600 transition-all duration-300"
                style={{ width: `${validationProgress}%` }}
              ></div>
            </div>
            <p className="mt-1 text-sm text-gray-600">
              Veriler doğrulanıyor... {validationProgress}%
            </p>
          </div>
        )}
        <MemoizedNavigation
          step={step}
          setStep={setStep}
          file={file}
          isProcessing={isProcessing}
          isSubmitting={isSubmitting}
          isMappingComplete={isMappingComplete()}
          processData={processData}
          handleSubmit={handleSubmit}
          processedData={processedData}
          validationErrors={validationErrors}
          isDataDirty={isDataDirty}
        />
      </div>
    </div>
  );
};

export default BulkImportWizard;
